/**
 * Shifts Settings Page (Phase 9.1)
 *
 * Manage organization shifts with CRUD operations.
 * Features search, filters, and modal form for create/edit.
 */

import { useState, useEffect } from 'react';
import {
  Clock,
  Plus,
  Search,
  AlertCircle,
  Loader2,
  Users,
  Pencil,
  Trash2,
  X,
} from 'lucide-react';
import { useShifts, useDeleteShift, useCreateShift, useUpdateShift } from '../../hooks/useShifts';
import { useCurrentUser } from '../../hooks/useUsers';
import type { ShiftWithCount, CreateShift } from '@guild/shared';
import { useDocumentTitle } from '../../lib/hooks/useDocumentTitle';
import { CrewRotationPanel } from './components/CrewRotationPanel';

/**
 * Format a 0-23 hour value as a 12-hour time string (e.g., 6 -> "6:00 AM", 14 -> "2:00 PM")
 */
function formatHour(hour: number): string {
  if (hour === 0) return '12:00 AM';
  if (hour === 12) return '12:00 PM';
  if (hour < 12) return `${hour}:00 AM`;
  return `${hour - 12}:00 PM`;
}

export function ShiftsPage() {
  useDocumentTitle('Shifts');
  // Phase 19.S frontend migration — read role from DB-canonical /api/me, not the JWT claim.
  const { data: currentUser } = useCurrentUser();
  const userRole = currentUser?.role;
  const isSuperAdmin = userRole === 'super_admin';
  const isAdmin = userRole === 'admin' || isSuperAdmin;

  const [searchQuery, setSearchQuery] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingShift, setEditingShift] = useState<ShiftWithCount | null>(null);

  const { data: shifts, isLoading, error } = useShifts({
    search: searchQuery || undefined,
    includeInactive: showInactive,
  });

  const deleteMutation = useDeleteShift();

  const handleCreate = () => {
    setEditingShift(null);
    setIsFormOpen(true);
  };

  const handleEdit = (shift: ShiftWithCount) => {
    setEditingShift(shift);
    setIsFormOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this shift?')) {
      await deleteMutation.mutateAsync(id);
    }
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setEditingShift(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-guild-mint/50">
            <Clock size={24} className="text-guild-green" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-gray-900">
              Shifts
            </h1>
            <p className="mt-0.5 text-sm text-gray-500">
              Manage organization shifts for grouping operators by work schedule
            </p>
          </div>
        </div>
        {isAdmin && (
          <button
            onClick={handleCreate}
            className="
              inline-flex items-center gap-2 rounded-xl
              bg-guild-green px-5 py-2.5
              text-sm font-semibold text-white
              shadow-lg shadow-guild-green/20
              hover:bg-guild-green/95 hover:shadow-xl hover:shadow-guild-green/25
              active:scale-[0.98]
              transition-all duration-200
            "
          >
            <Plus size={18} strokeWidth={2.5} />
            Add Shift
          </button>
        )}
      </div>

      {/* Filters Bar — only show when there is data or an active search */}
      {((shifts && shifts.length > 0) || searchQuery) && (
        <div className="flex flex-wrap items-center gap-4">
          {/* Search */}
          <div className="relative flex-1 min-w-[280px]">
            <Search
              size={18}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              placeholder="Search shifts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="
                w-full rounded-xl border border-gray-200 bg-white
                pl-11 pr-4 py-2.5 text-sm
                placeholder:text-gray-400
                focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
                transition-all duration-200
              "
            />
          </div>

          {/* Inactive filter */}
          {isAdmin && (
            <label className="flex items-center gap-2.5 px-4 py-2.5 rounded-xl border border-gray-200 bg-white cursor-pointer hover:border-gray-300 transition-colors">
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 bg-white text-guild-green focus:ring-guild-green focus:ring-offset-0"
              />
              <span className="text-sm font-medium text-gray-600">
                Show inactive
              </span>
            </label>
          )}
        </div>
      )}

      {/* Content */}
      {error ? (
        <div className="flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 p-4">
          <AlertCircle size={20} className="text-red-500 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-red-800">
              Failed to load shifts
            </p>
            <p className="text-sm text-red-600">
              Please try refreshing the page or contact support if the issue persists.
            </p>
          </div>
        </div>
      ) : isLoading ? (
        <div className="flex flex-col items-center justify-center py-16">
          <Loader2 size={32} className="text-guild-green animate-spin" />
          <p className="mt-4 text-sm text-gray-500">Loading shifts...</p>
        </div>
      ) : shifts && shifts.length > 0 ? (
        <ShiftList
          shifts={shifts}
          isAdmin={isAdmin}
          onEdit={handleEdit}
          onDelete={handleDelete}
          isDeleting={deleteMutation.isPending}
        />
      ) : (
        <EmptyState
          searchQuery={searchQuery}
          isAdmin={isAdmin}
          onCreateClick={handleCreate}
        />
      )}

      {/* Crew rotation (Feature 2) */}
      <CrewRotationPanel isAdmin={isAdmin} />

      {/* Form Dialog */}
      {isFormOpen && (
        <ShiftForm
          shift={editingShift}
          onClose={handleFormClose}
        />
      )}
    </div>
  );
}

// ============================================
// SHIFT LIST COMPONENT
// ============================================

interface ShiftListProps {
  shifts: ShiftWithCount[];
  isAdmin: boolean;
  onEdit: (shift: ShiftWithCount) => void;
  onDelete: (id: string) => void;
  isDeleting: boolean;
}

function ShiftList({ shifts, isAdmin, onEdit, onDelete, isDeleting }: ShiftListProps) {
  return (
    <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden">
      <table className="w-full">
        <thead>
          <tr className="border-b border-gray-100 bg-gray-50/50">
            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Shift
            </th>
            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Code
            </th>
            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Hours
            </th>
            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Description
            </th>
            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Operators
            </th>
            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Status
            </th>
            {isAdmin && (
              <th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            )}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {shifts.map((shift) => (
            <tr
              key={shift.id}
              className={`
                group transition-colors
                ${shift.isActive ? 'hover:bg-gray-50/50' : 'bg-gray-50/30'}
              `}
            >
              {/* Name */}
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center gap-3">
                  <div
                    className={`
                      flex h-9 w-9 items-center justify-center rounded-lg
                      ${shift.isActive ? 'bg-blue-50' : 'bg-gray-100'}
                    `}
                  >
                    <Clock
                      size={16}
                      className={shift.isActive ? 'text-blue-600' : 'text-gray-400'}
                    />
                  </div>
                  <span
                    className={`
                      text-sm font-semibold
                      ${shift.isActive ? 'text-gray-900' : 'text-gray-400'}
                    `}
                  >
                    {shift.name}
                  </span>
                </div>
              </td>

              {/* Code */}
              <td className="px-6 py-4 whitespace-nowrap">
                <code
                  className={`
                    text-xs font-mono px-2 py-1 rounded
                    ${shift.isActive ? 'bg-gray-100 text-gray-700' : 'bg-gray-50 text-gray-400'}
                  `}
                >
                  {shift.code}
                </code>
              </td>

              {/* Hours */}
              <td className="px-6 py-4 whitespace-nowrap">
                {shift.startHour != null || shift.endHour != null ? (
                  <span
                    className={`
                      text-sm
                      ${shift.isActive ? 'text-gray-600' : 'text-gray-400'}
                    `}
                  >
                    {shift.startHour != null && shift.endHour != null
                      ? `${formatHour(shift.startHour)} - ${formatHour(shift.endHour)}`
                      : shift.startHour != null
                        ? `${formatHour(shift.startHour)} - ?`
                        : `? - ${formatHour(shift.endHour!)}`}
                  </span>
                ) : (
                  <span className="text-sm italic text-gray-300">Not set</span>
                )}
              </td>

              {/* Description */}
              <td className="px-6 py-4">
                <div
                  className={`
                    text-sm max-w-xs truncate
                    ${shift.isActive ? 'text-gray-500' : 'text-gray-400'}
                  `}
                >
                  {shift.description || (
                    <span className="text-gray-300 italic">No description</span>
                  )}
                </div>
              </td>

              {/* Operator Count */}
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center gap-2">
                  <Users
                    size={16}
                    className={shift.isActive ? 'text-gray-400' : 'text-gray-300'}
                  />
                  <span
                    className={`
                      text-sm font-medium
                      ${shift.isActive ? 'text-gray-900' : 'text-gray-400'}
                    `}
                  >
                    {shift.operatorCount}
                  </span>
                </div>
              </td>

              {/* Status */}
              <td className="px-6 py-4 whitespace-nowrap">
                <span
                  className={`
                    inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold
                    ${shift.isActive
                      ? 'bg-emerald-50 text-emerald-700'
                      : 'bg-gray-100 text-gray-500'
                    }
                  `}
                >
                  <span
                    className={`
                      h-1.5 w-1.5 rounded-full
                      ${shift.isActive ? 'bg-emerald-500' : 'bg-gray-400'}
                    `}
                  />
                  {shift.isActive ? 'Active' : 'Inactive'}
                </span>
              </td>

              {/* Actions */}
              {isAdmin && (
                <td className="px-6 py-4 whitespace-nowrap text-right">
                  <div className="flex items-center justify-end gap-1">
                    <button
                      onClick={() => onEdit(shift)}
                      className="
                        inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5
                        text-sm font-medium text-gray-600
                        hover:bg-gray-100 hover:text-gray-900
                        transition-colors
                      "
                    >
                      <Pencil size={14} />
                      Edit
                    </button>
                    {shift.isActive && (
                      <button
                        onClick={() => onDelete(shift.id)}
                        disabled={isDeleting}
                        className="
                          inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5
                          text-sm font-medium text-red-600
                          hover:bg-red-50 hover:text-red-700
                          disabled:opacity-50 disabled:cursor-not-allowed
                          transition-colors
                        "
                      >
                        <Trash2 size={14} />
                        Delete
                      </button>
                    )}
                  </div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ============================================
// SHIFT FORM COMPONENT
// ============================================

interface ShiftFormProps {
  shift: ShiftWithCount | null;
  onClose: () => void;
}

function ShiftForm({ shift, onClose }: ShiftFormProps) {
  const isEdit = !!shift;
  const [name, setName] = useState(shift?.name ?? '');
  const [code, setCode] = useState(shift?.code ?? '');
  const [description, setDescription] = useState(shift?.description ?? '');
  const [startHour, setStartHour] = useState<string>(shift?.startHour != null ? String(shift.startHour) : '');
  const [endHour, setEndHour] = useState<string>(shift?.endHour != null ? String(shift.endHour) : '');
  const [error, setError] = useState<string | null>(null);

  const createMutation = useCreateShift();
  const updateMutation = useUpdateShift();

  const isPending = createMutation.isPending || updateMutation.isPending;

  // Reset form when shift changes
  useEffect(() => {
    setName(shift?.name ?? '');
    setCode(shift?.code ?? '');
    setDescription(shift?.description ?? '');
    setStartHour(shift?.startHour != null ? String(shift.startHour) : '');
    setEndHour(shift?.endHour != null ? String(shift.endHour) : '');
    setError(null);
  }, [shift]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    if (!code.trim()) {
      setError('Code is required');
      return;
    }

    if (!/^[a-z0-9_-]+$/i.test(code)) {
      setError('Code must be alphanumeric with underscores or hyphens');
      return;
    }

    // Validate hour values if provided
    const parsedStartHour = startHour !== '' ? parseInt(startHour, 10) : undefined;
    const parsedEndHour = endHour !== '' ? parseInt(endHour, 10) : undefined;

    if (parsedStartHour !== undefined && (isNaN(parsedStartHour) || parsedStartHour < 0 || parsedStartHour > 23)) {
      setError('Start hour must be between 0 and 23');
      return;
    }

    if (parsedEndHour !== undefined && (isNaN(parsedEndHour) || parsedEndHour < 0 || parsedEndHour > 23)) {
      setError('End hour must be between 0 and 23');
      return;
    }

    try {
      if (isEdit && shift) {
        await updateMutation.mutateAsync({
          id: shift.id,
          data: {
            name: name.trim(),
            code: code.trim().toLowerCase(),
            description: description.trim() || undefined,
            startHour: parsedStartHour,
            endHour: parsedEndHour,
          },
        });
      } else {
        const data: CreateShift = {
          name: name.trim(),
          code: code.trim().toLowerCase(),
          description: description.trim() || undefined,
          startHour: parsedStartHour,
          endHour: parsedEndHour,
        };
        await createMutation.mutateAsync(data);
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Dialog */}
      <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50">
              <Clock size={20} className="text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">
                {isEdit ? 'Edit Shift' : 'New Shift'}
              </h2>
              <p className="text-xs text-gray-500">
                {isEdit ? 'Update shift details' : 'Create a new shift'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="mb-5 flex items-center gap-2 rounded-xl bg-red-50 border border-red-100 px-4 py-3">
              <div className="h-2 w-2 rounded-full bg-red-500" />
              <p className="text-sm font-medium text-red-700">{error}</p>
            </div>
          )}

          <div className="space-y-5">
            {/* Name Field */}
            <div>
              <label
                htmlFor="name"
                className="block text-sm font-semibold text-gray-700 mb-2"
              >
                Name <span className="text-red-500">*</span>
              </label>
              <input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Day Shift"
                className="
                  w-full rounded-xl border border-gray-200 bg-white
                  px-4 py-3 text-sm
                  placeholder:text-gray-400
                  focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
                  disabled:bg-gray-50 disabled:text-gray-500
                  transition-all duration-200
                "
                disabled={isPending}
                autoFocus
              />
            </div>

            {/* Code Field */}
            <div>
              <label
                htmlFor="code"
                className="block text-sm font-semibold text-gray-700 mb-2"
              >
                Code <span className="text-red-500">*</span>
              </label>
              <input
                id="code"
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.toLowerCase())}
                placeholder="e.g., day"
                className="
                  w-full rounded-xl border border-gray-200 bg-white
                  px-4 py-3 text-sm font-mono
                  placeholder:text-gray-400
                  focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
                  disabled:bg-gray-50 disabled:text-gray-500
                  transition-all duration-200
                "
                disabled={isPending}
              />
              <p className="mt-1.5 text-xs text-gray-400">
                Short identifier for reports (letters, numbers, underscores, hyphens)
              </p>
            </div>

            {/* Description Field */}
            <div>
              <label
                htmlFor="description"
                className="block text-sm font-semibold text-gray-700 mb-2"
              >
                Description
                <span className="font-normal text-gray-400 ml-1">(optional)</span>
              </label>
              <textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Brief description of this shift..."
                rows={3}
                className="
                  w-full rounded-xl border border-gray-200 bg-white
                  px-4 py-3 text-sm
                  placeholder:text-gray-400
                  focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
                  disabled:bg-gray-50 disabled:text-gray-500
                  resize-none
                  transition-all duration-200
                "
                disabled={isPending}
              />
            </div>

            {/* Start Hour / End Hour Fields */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="startHour"
                  className="block text-sm font-semibold text-gray-700 mb-2"
                >
                  Start Hour
                </label>
                <input
                  id="startHour"
                  type="number"
                  min={0}
                  max={23}
                  value={startHour}
                  onChange={(e) => setStartHour(e.target.value)}
                  placeholder="e.g., 6"
                  className="
                    w-full rounded-xl border border-gray-200 bg-white
                    px-4 py-3 text-sm
                    placeholder:text-gray-400
                    focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
                    disabled:bg-gray-50 disabled:text-gray-500
                    transition-all duration-200
                  "
                  disabled={isPending}
                />
                <p className="mt-1.5 text-xs text-gray-400">
                  0 = 12:00 AM, 6 = 6:00 AM
                </p>
              </div>
              <div>
                <label
                  htmlFor="endHour"
                  className="block text-sm font-semibold text-gray-700 mb-2"
                >
                  End Hour
                </label>
                <input
                  id="endHour"
                  type="number"
                  min={0}
                  max={23}
                  value={endHour}
                  onChange={(e) => setEndHour(e.target.value)}
                  placeholder="e.g., 14"
                  className="
                    w-full rounded-xl border border-gray-200 bg-white
                    px-4 py-3 text-sm
                    placeholder:text-gray-400
                    focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
                    disabled:bg-gray-50 disabled:text-gray-500
                    transition-all duration-200
                  "
                  disabled={isPending}
                />
                <p className="mt-1.5 text-xs text-gray-400">
                  14 = 2:00 PM, 22 = 10:00 PM
                </p>
              </div>
              <p className="col-span-2 text-xs text-gray-500">
                Optional. A KPI result goes to its operator&apos;s assigned shift when the operator
                has one. Otherwise it goes to the shift whose hours contain its recording time or,
                if a crew rotation is set on this page, to the crew the rotation names. Results
                already saved keep their shift.
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="mt-6 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isPending}
              className="
                px-5 py-2.5 text-sm font-semibold text-gray-600
                rounded-xl hover:bg-gray-100
                disabled:opacity-50 disabled:cursor-not-allowed
                transition-colors
              "
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isPending}
              className="
                inline-flex items-center gap-2 px-5 py-2.5
                text-sm font-semibold text-white bg-guild-green
                rounded-xl shadow-lg shadow-guild-green/20
                hover:bg-guild-green/95 hover:shadow-xl hover:shadow-guild-green/25
                disabled:opacity-50 disabled:cursor-not-allowed
                active:scale-[0.98]
                transition-all duration-200
              "
            >
              {isPending && (
                <Loader2 size={16} className="animate-spin" />
              )}
              {isEdit ? 'Save Changes' : 'Create Shift'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================
// EMPTY STATE COMPONENT
// ============================================

interface EmptyStateProps {
  searchQuery: string;
  isAdmin: boolean;
  onCreateClick: () => void;
}

function EmptyState({ searchQuery, isAdmin, onCreateClick }: EmptyStateProps) {
  const isSearching = !!searchQuery;

  return (
    <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden">
      <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gray-50 mb-5">
          <Clock size={32} className="text-gray-300" />
        </div>

        <h3 className="text-lg font-semibold text-gray-900">
          {isSearching ? 'No shifts found' : 'No shifts yet'}
        </h3>

        <p className="mt-2 text-sm text-gray-500 max-w-sm">
          {isSearching
            ? `No shifts match "${searchQuery}". Try a different search term.`
            : 'Get started by creating your first shift to organize operators by work schedule.'}
        </p>

        {isAdmin && !isSearching && (
          <button
            onClick={onCreateClick}
            className="
              mt-6 inline-flex items-center gap-2 rounded-xl
              bg-guild-green px-5 py-2.5
              text-sm font-semibold text-white
              shadow-lg shadow-guild-green/20
              hover:bg-guild-green/95 hover:shadow-xl hover:shadow-guild-green/25
              active:scale-[0.98]
              transition-all duration-200
            "
          >
            <Plus size={18} strokeWidth={2.5} />
            Create First Shift
          </button>
        )}
      </div>
    </div>
  );
}
